import { EntityHandler, EntityListResult, SectionHandlerGroup, FormSectionHandler } from '../entity.handler';
import { IKuiTableCol } from 'app/key-ui';
import { Injectable } from '@angular/core';
import { DeviceResponse, DeviceCreateRequest, DeviceUpdateRequest } from '@key-telematics/fleet-api-client';
import { AdminEditorService } from '../../admin.service';
import { EMPTY_GUID, EnumToComboValues, GridColumnHelper } from '../utils';
import { DeviceDetailsSectionHandler } from './details.section';
import { CustomFieldsSectionHandler } from '../shared/info.section';
import { DeviceLinkedSectionHandler } from './linked.section';
import { DeviceParametersSectionHandler, getDeviceTypeIdFromBulk } from './parameters.section';
import { DeviceIoSectionHandler, InputTypeValues } from './io.section';
import { FormBuilderDefinition } from 'app/shared/components/form-builder';
import { CustomFormGroupSectionHandler } from '../shared/form.section';
import { ICONS } from '../icons';
import { RegisterEntityHandler } from '../../entities';
import { AccessorySectionHandler } from './accessory.section';
import { ModalService } from 'app/shared/components/modal';
import { AdminConsoleModalComponent } from '../../console/console.component';
import { DomSanitizer } from '@angular/platform-browser';
import { HealthIssueService } from '../healthissues/healthissue.service';
import { buildChartSectionGroup } from '../shared/chart.section';
import { DeviceIOWhitelistSectionHandler } from './io-whitelist.section';
import { getDeviceConfiguration } from 'app/services/api/api.utils';
import { DeviceBitmapsSectionHandler } from './bitmaps.section';
import { buildLogsSectionGroup } from '../shared/feed.section';

@RegisterEntityHandler('device')
@Injectable()
export class DeviceHandler extends EntityHandler<DeviceResponse> {

    constructor(
        admin: AdminEditorService,
        private modal: ModalService,
        private issues: HealthIssueService,
        private sanitizer: DomSanitizer
    ) {
        super(admin);
    }

    actions = [];

    states = ['inactive', 'active', 'suspended', 'deleted'];

    listEntities(ownerId: string, offset: number, limit: number, sort: string, filter: string): Promise<EntityListResult> {
        return this.admin.getCached(`listDevices/${ownerId}/${offset}/${limit}/${sort}/${filter}`, api => api.entities.listDevices(ownerId, offset, limit, sort, filter), 3).then(response => ({
            ...response,
            type: 'device',
            title: this.admin.i18n.instant('ADMIN.ENTITIES.DEVICES'),
        }));
    }

    getEntity(id: string): Promise<DeviceResponse> {
        this.actions = [];
        return this.admin.getCached(`getDevice/${id}`, api => api.entities.getDevice(id), 10, true).then(async device => {
            if (this.admin.user.owner.type !== 'client') {
                const config = await this.admin.getCached(`getDeviceConfiguration/${device.id}`, api => getDeviceConfiguration(api, device));
                if (config.features && (config.features.console || config.features.upload_settings)) {
                    this.actions = [
                        { icon: 'terminal', name: this.admin.translate('EDITORS.DEVICE.CONSOLE.TIP'), click: (d) => this.openConsole(d) },
                    ];
                }
            }
            return device;
        });
    }

    createEntity(ownerId: string, entity: DeviceCreateRequest): Promise<DeviceResponse> {
        entity.ownerId = ownerId;
        return this.admin.applyUpdate(api => api.entities.createDevice(entity));
    }

    setEntityState(entity: any, state: string): Promise<any> {
        return this.admin.applyUpdate(api => api.entities.updateDevice(entity.id, { state: state as any }));
    }

    getGridColumns(ownerId: string): Promise<IKuiTableCol[]> {

        return Promise.all([
            this.admin.getCached(`getClient/${ownerId}`, api => api.accounts.getClient(ownerId), 60),
            this.admin.getCached(`listDeviceTypes/${ownerId}`, api => api.entities.listDeviceTypes(ownerId)),
            DeviceDetailsSectionHandler.getAvailableRootDeviceTypeIdsForClient(this.admin, ownerId),
        ]).then(([client, deviceTypes, available]) => {

            const grid = new GridColumnHelper(this.admin.i18n, 'ADMIN.EDITORS.DEVICE.COLUMNS');

            const deviceTypeValues = DeviceDetailsSectionHandler.filterAllowedDeviceTypes(ownerId, deviceTypes.items, available, []).map(({ key, value }) => ({ id: key, value }));

            const columns: IKuiTableCol[] = [
                grid.IconTextColumn('name', 'NAME', ICONS.device),
                grid.IdNameComboColumn('deviceType', 'DEVICE_TYPE', deviceTypeValues, 100),
                grid.ComboColumn('state', 'STATE', EnumToComboValues(this.admin.i18n, 'ADMIN.STATES', this.states, ['deleted']), 100),
                grid.IdNameTextColumn('asset', 'ASSET', 120),
                grid.IdNameTextColumn('simcard', 'SIM_CARD', 120),
                grid.IdNameTextColumn('configProfile', 'CONFIG_PROFILE', 120),
            ];

            (client.customFields['device'] || []).forEach(customField => {
                columns.push({
                    ...grid.TextColumn(`fields.${customField.id}`, customField.title, 100),
                    format: (_col, row) => {
                        return row.fields && row.fields[customField.id];
                    },
                });
            });

            columns.push(grid.TimeAgoColumn('modifiedDate', 'MODIFIED'));
            return columns;
        });

    }

    async getEditorSections(ownerId: string, entity: DeviceResponse): Promise<SectionHandlerGroup<DeviceResponse>[]> {

        let custom = [];
        const deviceTypeId = getDeviceTypeIdFromBulk(entity ? [entity] : this.admin.selectedItems);
        const deviceType = deviceTypeId ? await this.admin.getCached(`getDeviceType/${deviceTypeId}/${ownerId}`, api => api.entities.getDeviceType(deviceTypeId, ownerId)) : null;
        if (deviceType && deviceType.settingsDefinition) {
            try {
                const def = JSON.parse(deviceType.settingsDefinition) as FormBuilderDefinition;
                custom = def.groups.map(group => new CustomFormGroupSectionHandler(this.admin, ownerId, group, 'settings', (e, payload) => {
                    return this.admin.applyUpdate(api => api.entities.updateDevice(e.id, payload));
                }));
            } catch (err) {
                console.warn('Invalid device type settings definition', deviceType.settingsDefinition);
                console.error(err);
            }
        }

        const isConfiguredByProfile = (entity: any): boolean => entity && entity.configProfile?.id && entity.configProfile?.id !== EMPTY_GUID;

        const hasConfigProfile = entity ? isConfiguredByProfile(entity) : this.admin.selectedItems?.some(isConfiguredByProfile);

        const applyUpdate = (entity: DeviceResponse, payload: DeviceUpdateRequest): Promise<DeviceResponse> => {
            return this.admin.applyUpdate(api => api.entities.updateDevice(entity.id, payload), { _type: 'device' });
        }

        const sections: SectionHandlerGroup<DeviceResponse>[] = [{
            name: this.admin.i18n.instant('ADMIN.EDITOR.INFORMATION'),
            sections: [
                new DeviceDetailsSectionHandler(this.admin, ownerId),
                new CustomFieldsSectionHandler<DeviceResponse>(this.admin, ownerId, 'device'),
                new DeviceLinkedSectionHandler(this.admin, ownerId),
            ],
            warnings: entity ? (await this.issues.getIssuesForEntity(ownerId, entity.id, 1)).map(issue => {
                return this.sanitizer.bypassSecurityTrustHtml(`${issue.description}<br><a href="/#/admin/client/${entity.owner.id}/healthissues/grid/healthissue/${issue.id}">More</a>`);
            }) : [],
        }];

        if (!hasConfigProfile) {

            sections.push(...[{
                name: this.admin.i18n.instant('ADMIN.EDITOR.SETTINGS'),
                sections: [
                    new DeviceParametersSectionHandler(this.admin, ownerId, applyUpdate.bind(this)),
                    ...custom,
                ],
                message: !deviceType ? this.admin.i18n.instant('ADMIN.EDITORS.DEVICE.MESSAGES.NO_BULK') : null,
            }, {
                name: this.admin.i18n.instant('ADMIN.EDITOR.IO'),
                sections: [
                    new DeviceIOWhitelistSectionHandler(this.admin, ownerId, applyUpdate.bind(this)),
                    new DeviceBitmapsSectionHandler(this.admin, ownerId, applyUpdate.bind(this)),
                    ...InputTypeValues.map(iotype => new DeviceIoSectionHandler(this.admin, ownerId, iotype, applyUpdate.bind(this))),
                ],
                message: !deviceType ? this.admin.i18n.instant('ADMIN.EDITORS.DEVICE.MESSAGES.NO_BULK') : null,
            }]);

            if (deviceType && deviceType.accessories && Object.keys(deviceType.accessories).length > 0) {

                sections.push({
                    name: this.admin.i18n.instant('ADMIN.EDITOR.ACCESSORIES'),
                    sections: Object.keys(deviceType.accessories).map(accId => new AccessorySectionHandler(this.admin, ownerId, accId, deviceType.accessories[accId], applyUpdate.bind(this))).sort((a, b) => a.title.localeCompare(b.title)),
                    message: undefined,
                });
            }
        }

        if (this.admin.user.owner.type === 'system') {
            sections.push(buildLogsSectionGroup(this.admin, 'device')),
            sections.push(buildChartSectionGroup(this.admin, 'device', ['record', 'byte', 'connection'], ['24h', '7d', '30d', '60d', '90d']));
        }


        return sections;

    }

    getCreationSections(ownerId: string): Promise<{ entityType: string, entity: DeviceUpdateRequest, sections: FormSectionHandler<DeviceResponse, any>[] }> {
        return Promise.resolve({
            entityType: 'device',
            entity: {}, // set up some defaults if you want
            sections: [
                new DeviceDetailsSectionHandler(this.admin, ownerId),
                new CustomFieldsSectionHandler<DeviceResponse>(this.admin, ownerId, 'device'),
            ],
        });
    }


    async openConsole(device: DeviceResponse) {

        if (device) {
            await new Promise<void>((resolve, _reject) => {
                this.modal.open(AdminConsoleModalComponent, {
                    data: {
                        deviceId: device.id,
                    },
                    actions: {
                        close: () => {
                            this.modal.close();
                            resolve();
                        },
                    },
                });
            });
        }

    }

}













